import { createClient } from '@supabase/supabase-js';
import { Patient, TestResult, Clinician, Evolutivo } from '../types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('⚠️ Supabase credentials not found. Using localStorage fallback.');
}

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        flowType: 'pkce'
      }
    })
  : null;

// Database types for Supabase
export type Database = {
  public: {
    Tables: {
      patients: {
        Row: {
          id: string;
          user_id: string | null; // NEW: Reference to auth.users
          name: string;
          date_of_birth: string | null;
          gender: string | null;
          email: string | null;
          phone: string | null;
          medical_record_number: string | null;
          notes: string | null;
          created_at: string;
          updated_at: string;
          archived: boolean;
          // Demographic
          education_level: string | null;
          occupation: string | null;
          marital_status: string | null;
          socioeconomic_context: string | null;
          living_arrangement: string | null;
          // Psychiatric History
          previous_diagnoses: string | null;
          previous_treatments: string | null;
          psychiatric_hospitalizations: string | null;
          suicide_attempts: string | null;
          // Medical History
          chronic_conditions: string | null;
          current_medications: string | null;
          head_trauma_history: string | null;
          substance_use: string | null;
          // Family History
          family_mental_health_history: string | null;
          family_suicide_history: string | null;
          // Current Consultation
          consultation_reason: string | null;
          symptom_duration: string | null;
          recent_stressors: string | null;
          functional_impact: string | null;
          // Previous Evaluations
          previous_test_results: string | null;
          previous_reports: string | null;
          formal_diagnoses: string | null;
          // Clinical Observations
          collaboration_level: string | null;
          fatigue_observations: string | null;
          spontaneous_comments: string | null;
          observable_behavior: string | null;
        };
        Insert: Omit<Database['public']['Tables']['patients']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['patients']['Insert']>;
      };
      test_results: {
        Row: {
          id: string;
          user_id: string | null; // NEW: Reference to auth.users
          patient_id: string;
          test_id: string;
          test_code: string;
          test_name: string;
          test_category: string;
          date: string;
          answers: Record<string, number>;
          total_score: number;
          subscores: Record<string, number> | null;
          severity_label: string;
          ai_analysis: string | null;
          clinician_notes: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['test_results']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['test_results']['Insert']>;
      };
      clinicians: {
        Row: {
          id: string;
          user_id: string | null; // NEW: Reference to auth.users
          created_by_admin_id: string | null;
          name: string;
          email: string;
          role: string;
          avatar_url: string | null;
          phone: string | null;
          license_number: string | null;
          specialization: string | null;
          active: boolean;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['clinicians']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['clinicians']['Insert']>;
      };
      evolutivos: {
        Row: {
          id: string;
          user_id: string;
          patient_id: string;
          date: string;
          content: string;
          linked_test_result_id: string | null;
          analysis_instructions: string | null;
          ai_analysis: {
            text: string;
            timestamp: string;
            modelUsed: string;
          } | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['evolutivos']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['evolutivos']['Insert']>;
      };
    };
  };
};

// Helper to check if Supabase is available
export const isSupabaseAvailable = () => supabase !== null;

// Convert database row to Patient type
export const dbToPatient = (row: Database['public']['Tables']['patients']['Row']): Patient => ({
  id: row.id,
  userId: row.user_id || undefined,
  name: row.name,
  dateOfBirth: row.date_of_birth || undefined,
  gender: row.gender as Patient['gender'],
  email: row.email || undefined,
  phone: row.phone || undefined,
  medicalRecordNumber: row.medical_record_number || undefined,
  notes: row.notes || undefined,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
  archived: row.archived,
  // Demographic
  educationLevel: row.education_level || undefined,
  occupation: row.occupation || undefined,
  maritalStatus: row.marital_status || undefined,
  socioeconomicContext: row.socioeconomic_context || undefined,
  livingArrangement: row.living_arrangement || undefined,
  // Psychiatric History
  previousDiagnoses: row.previous_diagnoses || undefined,
  previousTreatments: row.previous_treatments || undefined,
  psychiatricHospitalizations: row.psychiatric_hospitalizations || undefined,
  suicideAttempts: row.suicide_attempts || undefined,
  // Medical History
  chronicConditions: row.chronic_conditions || undefined,
  currentMedications: row.current_medications || undefined,
  headTraumaHistory: row.head_trauma_history || undefined,
  substanceUse: row.substance_use || undefined,
  // Family History
  familyMentalHealthHistory: row.family_mental_health_history || undefined,
  familySuicideHistory: row.family_suicide_history || undefined,
  // Current Consultation
  consultationReason: row.consultation_reason || undefined,
  symptomDuration: row.symptom_duration || undefined,
  recentStressors: row.recent_stressors || undefined,
  functionalImpact: row.functional_impact || undefined,
  // Previous Evaluations
  previousTestResults: row.previous_test_results || undefined,
  previousReports: row.previous_reports || undefined,
  formalDiagnoses: row.formal_diagnoses || undefined,
  // Clinical Observations
  collaborationLevel: row.collaboration_level || undefined,
  fatigueObservations: row.fatigue_observations || undefined,
  spontaneousComments: row.spontaneous_comments || undefined,
  observableBehavior: row.observable_behavior || undefined
});

// Convert database row to TestResult type
export const dbToTestResult = (row: Database['public']['Tables']['test_results']['Row']): TestResult => ({
  id: row.id,
  userId: row.user_id || undefined,
  patientId: row.patient_id,
  testId: row.test_id,
  testCode: row.test_code,
  testName: row.test_name,
  testCategory: row.test_category,
  date: row.date,
  answers: row.answers,
  totalScore: row.total_score,
  subscores: row.subscores || undefined,
  severityLabel: row.severity_label,
  aiAnalysis: row.ai_analysis || undefined,
  aiAnalysisHistory: (row as Database['public']['Tables']['test_results']['Row'] & { ai_analysis_history?: unknown[] }).ai_analysis_history || undefined,
  clinicianNotes: row.clinician_notes || undefined,
  createdAt: row.created_at,
  updatedAt: row.updated_at
});

// Convert Patient to database insert format
export const patientToDb = (patient: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>): Database['public']['Tables']['patients']['Insert'] => ({
  user_id: patient.userId || null,
  name: patient.name,
  date_of_birth: patient.dateOfBirth || null,
  gender: patient.gender || null,
  email: patient.email || null,
  phone: patient.phone || null,
  medical_record_number: patient.medicalRecordNumber || null,
  notes: patient.notes || null,
  archived: patient.archived,
  // Demographic
  education_level: patient.educationLevel || null,
  occupation: patient.occupation || null,
  marital_status: patient.maritalStatus || null,
  socioeconomic_context: patient.socioeconomicContext || null,
  living_arrangement: patient.livingArrangement || null,
  // Psychiatric History
  previous_diagnoses: patient.previousDiagnoses || null,
  previous_treatments: patient.previousTreatments || null,
  psychiatric_hospitalizations: patient.psychiatricHospitalizations || null,
  suicide_attempts: patient.suicideAttempts || null,
  // Medical History
  chronic_conditions: patient.chronicConditions || null,
  current_medications: patient.currentMedications || null,
  head_trauma_history: patient.headTraumaHistory || null,
  substance_use: patient.substanceUse || null,
  // Family History
  family_mental_health_history: patient.familyMentalHealthHistory || null,
  family_suicide_history: patient.familySuicideHistory || null,
  // Current Consultation
  consultation_reason: patient.consultationReason || null,
  symptom_duration: patient.symptomDuration || null,
  recent_stressors: patient.recentStressors || null,
  functional_impact: patient.functionalImpact || null,
  // Previous Evaluations
  previous_test_results: patient.previousTestResults || null,
  previous_reports: patient.previousReports || null,
  formal_diagnoses: patient.formalDiagnoses || null,
  // Clinical Observations
  collaboration_level: patient.collaborationLevel || null,
  fatigue_observations: patient.fatigueObservations || null,
  spontaneous_comments: patient.spontaneousComments || null,
  observable_behavior: patient.observableBehavior || null
});

// Convert TestResult to database insert format
export const testResultToDb = (result: Omit<TestResult, 'id' | 'createdAt' | 'updatedAt' | 'patientName'>): Database['public']['Tables']['test_results']['Insert'] => {
  const dbRecord: Record<string, unknown> = {
    user_id: result.userId || null,
    patient_id: result.patientId,
    test_id: result.testId,
    test_code: result.testCode,
    test_name: result.testName,
    test_category: result.testCategory,
    date: result.date,
    answers: result.answers,
    total_score: result.totalScore,
    subscores: result.subscores || null,
    severity_label: result.severityLabel,
    ai_analysis: result.aiAnalysis || null,
    clinician_notes: result.clinicianNotes || null
  };

  // Add ai_analysis_history if it exists (field may not be in database yet)
  if (result.aiAnalysisHistory) {
    dbRecord.ai_analysis_history = result.aiAnalysisHistory;
  }

  return dbRecord as Database['public']['Tables']['test_results']['Insert'];
};

// Convert database row to Clinician type
export const dbToClinician = (row: Database['public']['Tables']['clinicians']['Row']): Clinician => ({
  id: row.id,
  userId: row.user_id || undefined,
  createdByAdminId: row.created_by_admin_id || undefined,
  name: row.name,
  email: row.email,
  role: row.role as Clinician['role'],
  avatarUrl: row.avatar_url || undefined,
  phone: row.phone || undefined,
  licenseNumber: row.license_number || undefined,
  specialization: row.specialization || undefined,
  clinicianType: (row as Record<string, unknown>).clinician_type as string | undefined || undefined,
  maxClinicians: (row as Record<string, unknown>).max_clinicians as number | undefined,
  evolutivosEnabled: (row as Record<string, unknown>).evolutivos_enabled as boolean ?? false,
  active: row.active,
  createdAt: row.created_at,
  updatedAt: row.updated_at
});

// Convert Clinician to database insert format
export const clinicianToDb = (clinician: Omit<Clinician, 'id' | 'createdAt' | 'updatedAt'>): Database['public']['Tables']['clinicians']['Insert'] => ({
  user_id: clinician.userId || null,
  created_by_admin_id: clinician.createdByAdminId || null,
  name: clinician.name,
  email: clinician.email,
  role: clinician.role,
  avatar_url: clinician.avatarUrl || null,
  phone: clinician.phone || null,
  license_number: clinician.licenseNumber || null,
  specialization: clinician.specialization || null,
  active: clinician.active
});

// Convert database row to Evolutivo type
export const dbToEvolutivo = (row: Database['public']['Tables']['evolutivos']['Row']): Evolutivo => ({
  id: row.id,
  userId: row.user_id,
  patientId: row.patient_id,
  date: row.date,
  content: row.content,
  linkedTestResultId: row.linked_test_result_id || undefined,
  analysisInstructions: row.analysis_instructions || undefined,
  aiAnalysis: row.ai_analysis || undefined,
  createdAt: row.created_at,
  updatedAt: row.updated_at
});

// Convert Evolutivo to database insert format
export const evolutivoToDb = (evolutivo: Omit<Evolutivo, 'id' | 'createdAt' | 'updatedAt'>): Database['public']['Tables']['evolutivos']['Insert'] => ({
  user_id: evolutivo.userId,
  patient_id: evolutivo.patientId,
  date: evolutivo.date,
  content: evolutivo.content,
  linked_test_result_id: evolutivo.linkedTestResultId || null,
  analysis_instructions: evolutivo.analysisInstructions || null,
  ai_analysis: evolutivo.aiAnalysis || null
});
